import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  GripVertical,
  Eye,
  EyeOff,
  Settings,
  Globe,
  Building,
  Users,
  Shield,
  Upload,
  Download
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface MetadataConfig {
  id: number;
  category: string;
  value: string;
  display_name: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
  metadata?: {
    parent?: string;
    [key: string]: any;
  };
  created_at: string;
  updated_at: string;
}

interface GroupedConfigs {
  [category: string]: MetadataConfig[];
}

interface BulkUploadResult {
  total: number;
  created: number;
  duplicates: number;
  failed: number;
  errors: Array<{
    row: number;
    category: string;
    value: string;
    error: string;
  }>;
}

const ProcessMetadata: React.FC = () => {
  const [configurations, setConfigurations] = useState<GroupedConfigs>({});
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['performing_country']));
  const [searchTerms, setSearchTerms] = useState<{ [category: string]: string }>({});
  const [selectedItems, setSelectedItems] = useState<{ [category: string]: Set<number> }>({});
  const [showBulkUpload, setShowBulkUpload] = useState<{ [category: string]: boolean }>({});
  const [bulkFile, setBulkFile] = useState<{ [category: string]: File | null }>({});
  const [uploadingBulk, setUploadingBulk] = useState<{ [category: string]: boolean }>({});
  const [bulkResult, setBulkResult] = useState<{ [category: string]: BulkUploadResult | null }>({});

  const categoryConfig = {
    // OS Level Categories (for RCSA organizational structure)
    os_level_1: {
      label: 'OS Level 1',
      icon: Building,
      description: 'Top-level organizational structure',
      color: 'bg-purple-600'
    },
    os_level_2: {
      label: 'OS Level 2',
      icon: Building,
      description: 'Second-level organizational structure',
      color: 'bg-purple-500'
    },
    os_level_3: {
      label: 'OS Level 3',
      icon: Building,
      description: 'Third-level organizational structure',
      color: 'bg-purple-400'
    },
    process_taxonomy: {
      label: 'Process Taxonomy',
      icon: Settings,
      description: 'Process classification categories',
      color: 'bg-cyan-500'
    },
    // Country and Legal Entity
    performing_country: {
      label: 'Performing Countries',
      icon: Globe,
      description: 'Countries where process steps are performed',
      color: 'bg-blue-500'
    },
    performing_legal_entity: {
      label: 'Performing Legal Entities',
      icon: Building,
      description: 'Legal entities that perform process steps',
      color: 'bg-green-500'
    },
    affected_country: {
      label: 'Affected Countries',
      icon: Globe,
      description: 'Countries affected by process steps',
      color: 'bg-blue-400'
    },
    affected_legal_entity: {
      label: 'Affected Legal Entities',
      icon: Building,
      description: 'Legal entities affected by process steps',
      color: 'bg-green-400'
    },
    // Product & Application
    product_inventory: {
      label: 'Product Inventory',
      icon: Shield,
      description: 'Products associated with processes',
      color: 'bg-amber-500'
    },
    applications: {
      label: 'Applications',
      icon: Settings,
      description: 'Supporting applications and systems',
      color: 'bg-teal-500'
    },
    // Models & Third Parties
    supporting_models: {
      label: 'Supporting Models',
      icon: Settings,
      description: 'Models that support process steps',
      color: 'bg-red-500'
    },
    third_party_relationship_type: {
      label: 'Third Party Relationship Types',
      icon: Users,
      description: 'Types of third-party relationships',
      color: 'bg-indigo-500'
    },
    // Business Functions & RCSA
    business_functions: {
      label: 'Business Functions',
      icon: Users,
      description: 'Business functions related to processes',
      color: 'bg-rose-500'
    },
    rcsa_process_id: {
      label: 'RCSA Process IDs',
      icon: Shield,
      description: 'Reference RCSA process identifiers',
      color: 'bg-violet-500'
    }
  };

  const fetchConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/process-metadata-config/');
      
      if (response.data.success) {
        setConfigurations(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch configurations');
      }
    } catch (error: any) {
      console.error('Error fetching configurations:', error);
      toast.error('Failed to load process metadata configurations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  const handleCreate = async (category: string) => {
    try {
      // Ensure the category is expanded first
      if (!expandedCategories.has(category)) {
        setExpandedCategories(prev => {
          const newSet = new Set(prev);
          newSet.add(category);
          return newSet;
        });
      }

      const timestamp = Date.now();
      const newConfig = {
        category,
        value: `NEW_${timestamp}`,
        display_name: 'New Item',
        description: '',
        sort_order: (configurations[category]?.length || 0) + 1
      };

      const response = await api.post('/api/process-metadata-config/', newConfig);

      if (response.data.success) {
        toast.success('Configuration created successfully');
        await fetchConfigurations();
        setEditingId(response.data.data.id);
      } else {
        throw new Error(response.data.message || 'Failed to create configuration');
      }
    } catch (error: any) {
      console.error('Error creating configuration:', error);
      toast.error('Failed to create configuration');
    }
  };

  const handleUpdate = async (id: number, updates: Partial<MetadataConfig>) => {
    try {
      const response = await api.put(`/api/process-metadata-config/${id}`, updates);
      
      if (response.data.success) {
        toast.success('Configuration updated successfully');
        await fetchConfigurations();
        setEditingId(null);
      } else {
        throw new Error(response.data.message || 'Failed to update configuration');
      }
    } catch (error: any) {
      console.error('Error updating configuration:', error);
      toast.error('Failed to update configuration');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this configuration?')) return;

    try {
      const response = await api.delete(`/api/process-metadata-config/${id}`);
      
      if (response.data.success) {
        toast.success('Configuration deleted successfully');
        await fetchConfigurations();
      } else {
        throw new Error(response.data.message || 'Failed to delete configuration');
      }
    } catch (error: any) {
      console.error('Error deleting configuration:', error);
      toast.error('Failed to delete configuration');
    }
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);

    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);

    // ✅ Scroll to top of section on EVERY click (expand or collapse)
    setTimeout(() => {
      const element = document.getElementById(`category-${category}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100); // Small delay for smooth animation
  };

  const filteredConfigurations = (configs: MetadataConfig[], category: string) => {
    let filtered = showInactive ? configs : configs.filter(config => config.is_active);

    // Apply search filter
    const searchTerm = searchTerms[category]?.toLowerCase() || '';
    if (searchTerm) {
      filtered = filtered.filter(config =>
        config.display_name.toLowerCase().includes(searchTerm) ||
        config.value.toLowerCase().includes(searchTerm) ||
        config.description?.toLowerCase().includes(searchTerm)
      );
    }

    return filtered;
  };

  const handleSearchChange = (category: string, value: string) => {
    setSearchTerms(prev => ({ ...prev, [category]: value }));
  };

  const toggleSelectItem = (category: string, itemId: number) => {
    setSelectedItems(prev => {
      const categorySet = new Set(prev[category] || []);
      if (categorySet.has(itemId)) {
        categorySet.delete(itemId);
      } else {
        categorySet.add(itemId);
      }
      return { ...prev, [category]: categorySet };
    });
  };

  const toggleSelectAll = (category: string, allIds: number[]) => {
    setSelectedItems(prev => {
      const categorySet = new Set(prev[category] || []);
      if (categorySet.size === allIds.length) {
        // Deselect all
        return { ...prev, [category]: new Set() };
      } else {
        // Select all
        return { ...prev, [category]: new Set(allIds) };
      }
    });
  };

  const handleBulkDelete = async (category: string) => {
    const selectedIds = Array.from(selectedItems[category] || []);
    if (selectedIds.length === 0) {
      toast.error('No items selected');
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedIds.length} item(s)?`)) return;

    try {
      await Promise.all(selectedIds.map(id =>
        api.delete(`/api/process-metadata-config/${id}`)
      ));

      toast.success(`Successfully deleted ${selectedIds.length} item(s)`);
      await fetchConfigurations();
      setSelectedItems(prev => ({ ...prev, [category]: new Set() }));
    } catch (error: any) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete some items');
    }
  };

  const handleBulkToggleActive = async (category: string, isActive: boolean) => {
    const selectedIds = Array.from(selectedItems[category] || []);
    if (selectedIds.length === 0) {
      toast.error('No items selected');
      return;
    }

    try {
      await Promise.all(selectedIds.map(id =>
        api.put(`/api/process-metadata-config/${id}`, { is_active: isActive })
      ));

      toast.success(`Successfully ${isActive ? 'activated' : 'deactivated'} ${selectedIds.length} item(s)`);
      await fetchConfigurations();
      setSelectedItems(prev => ({ ...prev, [category]: new Set() }));
    } catch (error: any) {
      console.error('Bulk toggle error:', error);
      toast.error('Failed to update some items');
    }
  };

  const handleBulkUpload = async (category: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkFile[category] || uploadingBulk[category]) return;

    try {
      setUploadingBulk(prev => ({ ...prev, [category]: true }));
      const formData = new FormData();
      formData.append('file', bulkFile[category]!);

      const response = await api.post('/api/process-metadata-config/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setBulkResult(prev => ({ ...prev, [category]: response.data.data }));
        setBulkFile(prev => ({ ...prev, [category]: null }));
        toast.success(response.data.message);
        await fetchConfigurations();
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Bulk upload failed:', error);
      toast.error(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploadingBulk(prev => ({ ...prev, [category]: false }));
    }
  };

  const downloadTemplate = (category: string) => {
    const templates: { [key: string]: { headers: string; sample: string } } = {
      // OS Level Categories
      os_level_1: {
        headers: 'Category,Value,Display Name,Description',
        sample: `os_level_1,FINANCE,Finance,Finance and Accounting Division\nos_level_1,OPERATIONS,Operations,Operations and Technology Division\nos_level_1,RISK,Risk Management,Risk Management Division`
      },
      os_level_2: {
        headers: 'Category,Value,Display Name,Parent (OS Level 1),Description',
        sample: `os_level_2,FIN_ACCOUNTING,Accounting,FINANCE,Accounting Department\nos_level_2,FIN_TREASURY,Treasury,FINANCE,Treasury Department\nos_level_2,OPS_IT,Information Technology,OPERATIONS,IT Department`
      },
      os_level_3: {
        headers: 'Category,Value,Display Name,Parent (OS Level 2),Description',
        sample: `os_level_3,ACC_AP,Accounts Payable,FIN_ACCOUNTING,Accounts Payable Team\nos_level_3,ACC_AR,Accounts Receivable,FIN_ACCOUNTING,Accounts Receivable Team\nos_level_3,IT_DEV,Development,OPS_IT,Software Development Team`
      },
      process_taxonomy: {
        headers: 'Category,Value,Display Name,Description',
        sample: `process_taxonomy,CORE,Core Process,Core business processes\nprocess_taxonomy,SUPPORT,Support Process,Supporting business processes\nprocess_taxonomy,MANAGEMENT,Management Process,Management and governance processes`
      },
      // Country Categories
      performing_country: {
        headers: 'Category,Value,Display Name,Country ID,Official Name,Region,Description',
        sample: `performing_country,US,United States,USA,United States of America,North America,US operations\nperforming_country,UK,United Kingdom,GBR,United Kingdom of Great Britain,Europe,UK operations`
      },
      affected_country: {
        headers: 'Category,Value,Display Name,Country ID,Official Name,Region,Description',
        sample: `affected_country,CA,Canada,CAN,Canada,North America,Canadian market\naffected_country,FR,France,FRA,French Republic,Europe,French market`
      },
      // Legal Entity Categories
      performing_legal_entity: {
        headers: 'Category,Value,Display Name,Country,Registered Office,Company Type,Business Description/Purpose,Company Status (code),LE Status,Ownership Status',
        sample: `performing_legal_entity,CORP_US,US Corporation,United States,New York Office,Corporation,Primary US entity,Active,Operating,Wholly Owned\nperforming_legal_entity,CORP_UK,UK Corporation,United Kingdom,London Office,Limited,UK subsidiary,Active,Operating,Subsidiary`
      },
      affected_legal_entity: {
        headers: 'Category,Value,Display Name,Country,Registered Office,Company Type,Business Description/Purpose,Company Status (code),LE Status,Ownership Status',
        sample: `affected_legal_entity,ENTITY_CA,Canadian Entity,Canada,Toronto Office,Corporation,Canadian operations,Active,Operating,Subsidiary\naffected_legal_entity,ENTITY_FR,French Entity,France,Paris Office,SARL,French operations,Active,Operating,Joint Venture`
      },
      // Product Inventory
      product_inventory: {
        headers: 'Category,Value,Display Name,Product_Name,Market,Segment,Active_Status,Product Type,BU',
        sample: `product_inventory,PROD_001,Product A,Premium Savings Account,US,Retail,Active,Deposit Product,Consumer Banking\nproduct_inventory,PROD_002,Product B,Business Credit Card,Global,Commercial,Active,Credit Product,Business Banking`
      },
      // Applications
      applications: {
        headers: 'Category,Value,Display Name,Central ID - Application Name,Central ID,Application Name,Application Type,Application Life Cycle Stage,Overall Inherent Risk,Security Information Classification',
        sample: `applications,APP_001,Core Banking System,CBS-001,CBS001,Core Banking Platform,Enterprise,Production,High,Confidential\napplications,APP_002,Mobile App,MOB-001,MOB001,Customer Mobile Application,Mobile,Production,Medium,Internal`
      },
      // Supporting Models
      supporting_models: {
        headers: 'Category,Value,Display Name,ID,Model Name,Model ID,Markets,Model Impact Category,Model Purpose & Intended Use,Model Version ID,Model Type,First Published,Last Updated,Model Certification Date,Model Type Group,Model Version',
        sample: `supporting_models,MODEL_A,Risk Model A,M001,Risk Assessment Model,RM-001,US|UK,High,Risk scoring and assessment,V1.2,Quantitative,2024-01-01,2024-12-01,2024-11-15,Risk Models,1.2\nsupporting_models,MODEL_B,Compliance Model B,M002,Compliance Check Model,CM-002,Global,Medium,Regulatory compliance validation,V2.0,Rule-based,2023-06-01,2024-10-01,2024-09-20,Compliance Models,2.0`
      },
      // Third Parties
      third_party_relationship_type: {
        headers: 'Category,Value,Display Name,TLM,TLM ID,Third Party Name,Status,Third Party Family Name (L1),Third Party Name (L2),Overall Risk,Description,Country From,Country To',
        sample: `third_party_relationship_type,VENDOR,Vendor,Vendor Management,TLM001,ABC Vendor Inc,Active,Technology Vendors,Cloud Providers,Medium,Third party vendor for cloud services,US,Global\nthird_party_relationship_type,PARTNER,Business Partner,Partner Management,TLM002,XYZ Partners Ltd,Active,Strategic Partners,Distribution Partners,Low,Distribution partnership,UK,EU`
      },
      // Business Functions
      business_functions: {
        headers: 'Category,Value,Display Name,Business Function,Business Function ID,Business Function Name,Business Function Status,Overall Criticality Rating,Business Function Tier,Business Unit Level 3,Business Unit Level 2',
        sample: `business_functions,BF_001,Payment Processing,Payment Processing,BF001,Payment Processing Operations,Active,Critical,Tier 1,Transaction Processing,Operations\nbusiness_functions,BF_002,Customer Onboarding,Customer Onboarding,BF002,Customer Onboarding Services,Active,High,Tier 2,Customer Services,Retail Banking`
      },
      // RCSA Process ID
      rcsa_process_id: {
        headers: 'Category,Value,Display Name,Process ID - Process Name,Process ID,Process Name,Business Unit,Process Owner,Business Unit Control Manager,RCSA Status,Risk Assessment Unit (RAU)',
        sample: `rcsa_process_id,PROC_001,Loan Origination,PROC-001,PR001,Loan Origination Process,Lending,John Doe,Jane Smith,In Progress,Lending Operations\nrcsa_process_id,PROC_002,Account Opening,PROC-002,PR002,New Account Opening Process,Retail Banking,Alice Johnson,Bob Williams,Completed,Retail Operations`
      }
    };

    const template = templates[category] || {
      headers: 'Category,Value,Display Name,Description',
      sample: `${category},SAMPLE_VALUE,Sample Item,Sample description`
    };

    const csvContent = `${template.headers}\n${template.sample}`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${category}_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Process Metadata Configuration</h2>
          <p className="text-gray-600 mt-1">
            Manage dropdown options for process step metadata fields
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            {showInactive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            <span>{showInactive ? 'Hide Inactive' : 'Show Inactive'}</span>
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {Object.entries(categoryConfig).map(([category, config]) => {
          const categoryConfigs = configurations[category] || [];
          const filteredConfigs = filteredConfigurations(categoryConfigs, category);
          const isExpanded = expandedCategories.has(category);
          const IconComponent = config.icon;
          const selectedCount = selectedItems[category]?.size || 0;
          const allFilteredIds = filteredConfigs.map(c => c.id);

          return (
            <div
              key={category}
              id={`category-${category}`}
              className="bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleCategory(category)}
              >
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${config.color} text-white`}>
                    <IconComponent className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{config.label}</h3>
                    <p className="text-sm text-gray-600">{config.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">
                    {filteredConfigs.length} items
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!expandedCategories.has(category)) {
                        setExpandedCategories(prev => {
                          const newSet = new Set(prev);
                          newSet.add(category);
                          return newSet;
                        });
                      }
                      setShowBulkUpload(prev => ({ ...prev, [category]: !prev[category] }));
                    }}
                    className="p-1 rounded text-gray-400 hover:text-green-600 hover:bg-green-50"
                    title="Bulk Upload"
                  >
                    <Upload className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCreate(category);
                    }}
                    className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                    type="button"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                    <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-200 p-4">
                  {/* Bulk Upload Section */}
                  {showBulkUpload[category] && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">Bulk Upload for {config.label}</h4>
                        <button
                          onClick={() => setShowBulkUpload(prev => ({ ...prev, [category]: false }))}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Upload CSV or Excel file. Required: Category, Value, Display Name
                      </p>
                      <button
                        onClick={() => downloadTemplate(category)}
                        className="mb-3 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center gap-1"
                      >
                        <Download className="h-3 w-3" />
                        Download Template
                      </button>
                      <form onSubmit={(e) => handleBulkUpload(category, e)} className="space-y-3">
                        <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center">
                          <input
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={(e) => setBulkFile(prev => ({ ...prev, [category]: e.target.files?.[0] || null }))}
                            className="hidden"
                            id={`bulk-upload-${category}`}
                          />
                          <label
                            htmlFor={`bulk-upload-${category}`}
                            className="cursor-pointer px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 inline-block text-sm"
                          >
                            Choose File
                          </label>
                          {bulkFile[category] && (
                            <p className="mt-2 text-sm text-gray-600">
                              <strong>{bulkFile[category]!.name}</strong>
                            </p>
                          )}
                        </div>
                        <button
                          type="submit"
                          disabled={!bulkFile[category] || uploadingBulk[category]}
                          className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                        >
                          {uploadingBulk[category] ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" />
                              Upload
                            </>
                          )}
                        </button>
                      </form>
                      {bulkResult[category] && (
                        <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                          <div className="flex justify-between mb-2">
                            <span className="text-sm font-semibold">Results</span>
                            <button
                              onClick={() => setBulkResult(prev => ({ ...prev, [category]: null }))}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Clear
                            </button>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-center text-xs">
                            <div className="p-2 bg-blue-50 rounded">
                              <div className="font-bold text-blue-700">{bulkResult[category]!.total}</div>
                              <div className="text-gray-600">Total</div>
                            </div>
                            <div className="p-2 bg-green-50 rounded">
                              <div className="font-bold text-green-700">{bulkResult[category]!.created}</div>
                              <div className="text-gray-600">Created</div>
                            </div>
                            <div className="p-2 bg-yellow-50 rounded">
                              <div className="font-bold text-yellow-700">{bulkResult[category]!.duplicates}</div>
                              <div className="text-gray-600">Duplicates</div>
                            </div>
                            <div className="p-2 bg-red-50 rounded">
                              <div className="font-bold text-red-700">{bulkResult[category]!.failed}</div>
                              <div className="text-gray-600">Failed</div>
                            </div>
                          </div>
                          {bulkResult[category]!.errors.length > 0 && (
                            <div className="mt-2 max-h-32 overflow-y-auto">
                              <p className="text-xs font-semibold mb-1">Errors:</p>
                              {bulkResult[category]!.errors.map((err, idx) => (
                                <div key={idx} className="text-xs p-1 bg-red-50 rounded mb-1">
                                  Row {err.row}: {err.error}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Search and Bulk Actions Bar */}
                  <div className="mb-4 space-y-3">
                    {/* Search Box */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search by name, value, or description..."
                        value={searchTerms[category] || ''}
                        onChange={(e) => handleSearchChange(category, e.target.value)}
                        className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <svg
                        className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>

                    {/* Bulk Actions */}
                    {selectedCount > 0 && (
                      <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <span className="text-sm font-medium text-blue-900">
                          {selectedCount} item(s) selected
                        </span>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleBulkToggleActive(category, true)}
                            className="px-3 py-1.5 text-sm text-green-700 bg-green-100 hover:bg-green-200 rounded-md transition-colors"
                          >
                            Activate
                          </button>
                          <button
                            onClick={() => handleBulkToggleActive(category, false)}
                            className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                          >
                            Deactivate
                          </button>
                          <button
                            onClick={() => handleBulkDelete(category)}
                            className="px-3 py-1.5 text-sm text-red-700 bg-red-100 hover:bg-red-200 rounded-md transition-colors flex items-center space-x-1"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            <span>Delete</span>
                          </button>
                          <button
                            onClick={() => setSelectedItems(prev => ({ ...prev, [category]: new Set() }))}
                            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {filteredConfigs.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {searchTerms[category] ? 'No items match your search.' : 'No configurations found. Click the + button to add one.'}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Select All Checkbox */}
                      <div className="flex items-center p-2 bg-gray-50 rounded-md border border-gray-200">
                        <input
                          type="checkbox"
                          checked={selectedCount === allFilteredIds.length && allFilteredIds.length > 0}
                          onChange={() => toggleSelectAll(category, allFilteredIds)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label className="ml-2 text-sm font-medium text-gray-700">
                          Select All ({filteredConfigs.length})
                        </label>
                      </div>

                      {/* Items List */}
                      {filteredConfigs.map((item) => (
                        <ConfigurationItem
                          key={item.id}
                          item={item}
                          isEditing={editingId === item.id}
                          isSelected={selectedItems[category]?.has(item.id) || false}
                          onSelect={() => toggleSelectItem(category, item.id)}
                          onEdit={() => setEditingId(item.id)}
                          onSave={(updates) => handleUpdate(item.id, updates)}
                          onCancel={() => setEditingId(null)}
                          onDelete={() => handleDelete(item.id)}
                          allConfigurations={configurations}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface ConfigurationItemProps {
  item: MetadataConfig;
  isEditing: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onSave: (updates: Partial<MetadataConfig>) => void;
  onCancel: () => void;
  onDelete: () => void;
  allConfigurations: GroupedConfigs;
}

const ConfigurationItem: React.FC<ConfigurationItemProps> = ({
  item,
  isEditing,
  isSelected,
  onSelect,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  allConfigurations
}) => {
  const [formData, setFormData] = useState({
    value: item.value,
    display_name: item.display_name,
    description: item.description || '',
    is_active: item.is_active,
    metadata: item.metadata || {}
  });

  useEffect(() => {
    if (isEditing) {
      setFormData({
        value: item.value,
        display_name: item.display_name,
        description: item.description || '',
        is_active: item.is_active,
        metadata: item.metadata || {}
      });
    }
  }, [isEditing, item]);

  const handleSave = () => {
    if (!formData.value.trim() || !formData.display_name.trim()) {
      toast.error('Value and display name are required');
      return;
    }

    // Validate parent for OS Level 2 and 3
    if (item.category === 'os_level_2' && !formData.metadata?.parent) {
      toast.error('Parent (OS Level 1) is required for Level 2 items');
      return;
    }
    if (item.category === 'os_level_3' && !formData.metadata?.parent) {
      toast.error('Parent (OS Level 2) is required for Level 3 items');
      return;
    }

    onSave(formData);
  };

  if (isEditing) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Value
            </label>
            <input
              type="text"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Internal value"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Display name"
            />
          </div>
        </div>

        {/* Parent Selection for OS Level 2 (belongs to OS Level 1) */}
        {item.category === 'os_level_2' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Parent (OS Level 1) <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.metadata?.parent || ''}
              onChange={(e) => setFormData({
                ...formData,
                metadata: { ...formData.metadata, parent: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select Parent OS Level 1</option>
              {(allConfigurations.os_level_1 || []).map((l1) => (
                <option key={l1.value} value={l1.display_name}>
                  {l1.display_name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              This L2 item will only show when the selected L1 parent is chosen in RCSA Builder
            </p>
          </div>
        )}

        {/* Parent Selection for OS Level 3 (belongs to OS Level 2) */}
        {item.category === 'os_level_3' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Parent (OS Level 2) <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.metadata?.parent || ''}
              onChange={(e) => setFormData({
                ...formData,
                metadata: { ...formData.metadata, parent: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select Parent OS Level 2</option>
              {(allConfigurations.os_level_2 || []).map((l2) => (
                <option key={l2.value} value={l2.value}>
                  {l2.display_name} (Parent: {l2.metadata?.parent || 'None'})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              This L3 item will only show when the selected L2 parent is chosen in RCSA Builder
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (Optional)
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            rows={2}
            placeholder="Optional description"
          />
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Active</span>
          </label>
          <div className="flex items-center space-x-2">
            <button
              onClick={onCancel}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              <Save className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors ${
      !item.is_active ? 'opacity-60 bg-gray-50' : 'bg-white'
    } ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
      <div className="flex items-center space-x-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          onClick={(e) => e.stopPropagation()}
        />
        <GripVertical className="h-4 w-4 text-gray-400" />
        <div>
          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-900">{item.display_name}</span>
            {!item.is_active && (
              <span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded">
                Inactive
              </span>
            )}
          </div>
          <div className="text-sm text-gray-600">
            Value: <code className="bg-gray-100 px-1 rounded">{item.value}</code>
          </div>
          {item.description && (
            <p className="text-sm text-gray-500 mt-1">{item.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={onEdit}
          className="p-1 text-gray-400 hover:text-blue-600"
        >
          <Edit3 className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-1 text-gray-400 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ProcessMetadata;